import { useEffect, useState } from 'react';
import { adminWaitlist } from '../../services/adminClient.js';
import { T, PageHeader, card } from './ui.jsx';

// ─────────────────────────────────────────────────────────────────
// AdminSubmissions — waitlist form responses (read-only) with a per-row
// Email button (mailto, prefilled greeting). Reads via adminWaitlist()
// under the admin's auth (admin_read_waitlist_submissions RLS policy).
// ─────────────────────────────────────────────────────────────────

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};
// Struggle/option strings are stored as "Label — encouraging copy"; show the label.
const labelOf = (s) => String(s || '').split(' — ')[0].trim();
const firstNameOf = (name) => String(name || '').trim().split(/\s+/)[0] || 'there';

function Chip({ children, tint = T.slate }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: tint, background: 'rgba(0,0,0,0.04)', border: `1px solid ${T.border}`, borderRadius: 99, padding: '3px 9px' }}>
      {children}
    </span>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ minWidth: 0 }}>
      <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: T.slate, margin: '0 0 2px' }}>{label}</p>
      <p style={{ fontSize: 13, color: T.ink, margin: 0, lineHeight: 1.45 }}>{value}</p>
    </div>
  );
}

function SubmissionCard({ s }) {
  const goals     = Array.isArray(s.goals) ? s.goals : [];
  const struggles = Array.isArray(s.biggest_struggle) ? s.biggest_struggle.map(labelOf) : [];
  const mailto = `mailto:${encodeURIComponent(s.email || '')}`
    + `?subject=${encodeURIComponent('AdaptOS — your spot is ready')}`
    + `&body=${encodeURIComponent(`Hi ${firstNameOf(s.name)},\n\n`)}`;

  return (
    <div style={{ ...card, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 15, fontWeight: 800, color: T.ink, margin: 0 }}>{s.name || '—'}</p>
          <p style={{ fontSize: 12.5, color: T.slate, margin: '3px 0 0' }}>
            {s.email || '—'}{s.phone ? ` · ${s.phone}` : ''} · {fmtDate(s.created_at)}
          </p>
        </div>
        {s.email && (
          <a href={mailto} style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', background: T.amber, color: '#fff', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700 }}>
            ✉ Email
          </a>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 14 }}>
        <Field label="Track interest" value={s.track_interest} />
        <Field label="Experience" value={s.experience_level} />
        <Field label="Availability" value={s.study_availability} />
        <Field label="Heard via" value={s.how_heard} />
      </div>

      {goals.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: T.slate, margin: '0 0 6px' }}>Goals</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{goals.map((g, i) => <Chip key={i} tint={T.emerald}>{g}</Chip>)}</div>
        </div>
      )}

      {struggles.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: T.slate, margin: '0 0 6px' }}>Biggest struggle</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{struggles.map((g, i) => <Chip key={i} tint={T.amber}>{g}</Chip>)}</div>
        </div>
      )}

      {s.personal_motivation && (
        <div style={{ marginTop: 12 }}>
          <Field label="Personal motivation" value={s.personal_motivation} />
        </div>
      )}
    </div>
  );
}

export default function AdminSubmissions() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let c = false;
    adminWaitlist().then(r => { if (!c) setRows(r || []); }).catch(() => { if (!c) setRows([]); });
    return () => { c = true; };
  }, []);

  return (
    <div>
      <PageHeader title="Submissions" subtitle="Waitlist form responses, newest first." />
      {rows === null ? (
        <p style={{ color: T.slate, fontSize: 13 }}>Loading submissions…</p>
      ) : rows.length === 0 ? (
        <div style={{ ...card }}><p style={{ fontSize: 13, color: T.slate, margin: 0 }}>No waitlist submissions yet.</p></div>
      ) : (
        <>
          <p style={{ fontSize: 12, color: T.slate, margin: '0 0 14px' }}>{rows.length} {rows.length === 1 ? 'response' : 'responses'}</p>
          {rows.map(s => <SubmissionCard key={s.id} s={s} />)}
        </>
      )}
    </div>
  );
}
